import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import secureLocalStorage from "react-secure-storage";
import he from 'he';
import { Button } from "@/components/ui/button";
import { handleDownloadCV } from "./handleDownloadCV";

// Final full View Profile component
// - Calls POST to: /personal-info, /additional-info, /education-list, /experience-list, /publication-list, /training-list, /certificate-list
// - Payload: { userId, password, user } from env + secureLocalStorage
// - Displays every field grouped exactly as requested (15 sections + E-signature)
// - Print-friendly styles included

export default function ProfileView() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = {
          userId: import.meta.env.VITE_API_USER,
          password: import.meta.env.VITE_API_PASS,
          user: secureLocalStorage.getItem("user"),
		  token:secureLocalStorage.getItem("token"),
        };

        const [personalRes, additionalRes, eduRes, expRes, pubRes, trainRes, certRes] = await Promise.all([
          axios.post(`${import.meta.env.VITE_API_BASE_URL}/personal-info`, payload),
          axios.post(`${import.meta.env.VITE_API_BASE_URL}/additional-info`, payload),
          axios.post(`${import.meta.env.VITE_API_BASE_URL}/education-list`, payload),
          axios.post(`${import.meta.env.VITE_API_BASE_URL}/experience-list`, payload),
          axios.post(`${import.meta.env.VITE_API_BASE_URL}/publication-list`, payload),
          axios.post(`${import.meta.env.VITE_API_BASE_URL}/training-list`, payload),
          axios.post(`${import.meta.env.VITE_API_BASE_URL}/certificate-list`, payload),
        ]);

        const personal = personalRes.data || {};
        const additional = additionalRes.data || {};

        const merged = {
          // from /personal-info
          personal: personal.personal || {},
          exEmployee: personal.exEmployee || {},
          permanentAddress: personal.permanentAddress || {},
          presentAddress: personal.presentAddress || {},

          // from /additional-info
          summary: additional.summary || "",
          objective: additional.objective || "",
          skills: additional.skills || [],
          hobbies: additional.hobbies || "",
          industries: additional.industries || "",
          salary: additional.salary || {},
          activities: additional.activities || "",
          declaration: additional.declaration || "",
          references: additional.references || [],
          languages: additional.languages || [],

          // lists
          education: Array.isArray(eduRes.data) ? eduRes.data : [],
          experience: Array.isArray(expRes.data) ? expRes.data : [],
          publications: Array.isArray(pubRes.data) ? pubRes.data : [],
          training: Array.isArray(trainRes.data) ? trainRes.data : [],
          certificates: Array.isArray(certRes.data) ? certRes.data : [],
        };

        setProfile(merged);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch profile data. Check console for details.");
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  const V = {
    container: { hidden: {}, visible: { transition: { staggerChildren: 0.04 } } },
    fadeUp: { hidden: { opacity: 0, y: 8 }, visible: { opacity: 1, y: 0, transition: { duration: 0.35 } } },
  };

  const fmt = (v) => (v === undefined || v === null || v === "" || v === "0000-00-00" ? "N/A" : v);

  if (loading) return <div className="p-6">Loading profile...</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;
  if (!profile) return <div className="p-6">No profile data available.</div>;

  const { personal, exEmployee, presentAddress, permanentAddress } = profile;

  // Section helper
  const section = (title, children, opts = {}) => (
    <motion.section variants={V.fadeUp} className={`bg-white/95 print:bg-white backdrop-blur-sm rounded-2xl p-6 shadow-sm border border-gray-100 print:border-0 print:shadow-none ${opts.className || ""}`}>
      <h3 className="text-lg font-semibold text-slate-700 mb-3 flex items-center gap-3 print:text-black">
        <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-indigo-500 to-sky-400 shadow" />
        {title}
      </h3>
      {children}
    </motion.section>
  );
	const preview=secureLocalStorage.getItem("picture");
	function textformat(textdata){
		let textinfo="";
		textdata = he.decode(textdata);
		textinfo=textdata.replace(/\\n/g, '<br />');
		textinfo=textinfo.split('\\n').join('\n');
		textinfo=textinfo.split('\n').join('<br />');
		textinfo=textinfo.split('\\r').join(' ');
		textinfo=textinfo.split('\\').join(' ');
		return textinfo;
	}
	
	
  return (
    <motion.div initial="hidden" animate="visible" variants={V.container} className="max-w-6xl mx-auto p-3 print:p-0 print:max-w-none; bg-gradient-to-b from-[#F3F4FF] via-[#E2E2FD] to-[#CECCFB]">
      
      {/* Header */}
      <div className="relative overflow-hidden rounded-3xl mb-6 print:rounded-none print:mb-4 bg-gradient-to-r from-[#1A2B63] via-[#0E1B40] to-[#0E1B40]">
        

        <div className="relative z-10 p-6 md:p-8">
          <motion.div variants={V.fadeUp} className="grid grid-cols-1 grid-cols-2 gap-6 items-center">
            {/* Left column: Name, category, address, mobile, email */}
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-white drop-shadow">{fmt(personal.name)}</h1>
              <p className="text-sm md:text-base text-white/90 mt-1">{fmt(personal.category_name)}</p>

              <div className="mt-4 text-white/90 space-y-1">
                <p className="text-sm">📍 {fmt(presentAddress?.fullAddress)}</p>
                <p className="text-sm">📞 {fmt(personal.contactNo)}</p>
                <p className="text-sm">✉️ {fmt(personal.email)}</p>
              </div>

              <div className="mt-4">
                <div className="h-1 w-40 bg-white/30 rounded-full" />				
              </div>
            </div>

            {/* Right column: Avatar placeholder (could be replaced with real avatar) */}
            <div className="flex justify-end items-start gap-4">
              <div className="w-36 h-36 md:w-44 md:h-44 bg-white rounded-2xl shadow-lg flex items-center justify-center overflow-hidden border-2 border-white">
				<img
					src={`${import.meta.env.VITE_API_SOURCE_URL}${preview}`}
					alt="Profile"
					
				/>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" className="opacity-80">
                  <circle cx="12" cy="8" r="3.2" stroke="#94a3b8" strokeWidth="1.2" />
                  <path d="M4 20c1.5-4 6.5-5 8-5s6.5 1 8 5" stroke="#94a3b8" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
              </div>
				
            </div>
          </motion.div>

          
        </div>
      </div>

      {/* Grid of sections - grouped exactly by rows requested */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Row 1: Split two columns already handled in header - but include a subtle border below */}
        <div className="md:col-span-2">
          <div className="border-b border-slate-200 pb-4 mb-4"></div>
        </div>

        {/* Row 2: Career Summary */}
        <div className="md:col-span-2">{section("Career Summary", <div className="text-sm text-slate-700" dangerouslySetInnerHTML={{__html: textformat(fmt(profile.summary))}}></div>)}</div>

        {/* Row 3: Career Objective */}
        <div className="md:col-span-2">{section("Career Objective", <div className="text-sm text-slate-700" dangerouslySetInnerHTML={{__html: textformat(fmt(profile.objective))}}></div>)}</div>

        {/* Row 4: Skills */}
        <div className="md:col-span-2">{section("Skills", <div className="flex flex-wrap gap-2">{(profile.skills || []).length ? profile.skills.map((s,i)=>(<span key={i} className="px-3 py-1 rounded-full text-xs font-medium border border-slate-200">{s}</span>)) : <span className="text-sm text-slate-500">N/A</span>}</div>)}</div>

        {/* Row 5: List of Experience */}
        <div className="md:col-span-2">
          {section(
            "Experience",
            <div className="space-y-3">
              {(profile.experience || []).length ? (
                profile.experience.map((e, i) => (
                  <div key={i} className="p-3 rounded-xl bg-slate-50 border border-gray-100">
                    <div className="flex flex-col md:flex-row md:justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold text-slate-800">{fmt(e.designation || e.role || e.position)}</div>
                        <div className="text-xs text-slate-500">{fmt(e.organization || e.company || e.employer)} {e.location ? `• ${e.location}` : ""}</div>
                      </div>
                      <div className="text-xs text-slate-400">{fmt(e.startDate)} — {fmt(e.endDate === "0000-00-00" || e.endDate === null ? (e.currentlyWorking || e.currently_working ? "Present" : e.endDate) : e.endDate)}</div>
                    </div>
                    <p className="text-xs text-slate-600 mt-2" dangerouslySetInnerHTML={{__html: textformat(fmt(e.responsibility || e.responsibilities))}}></p>
                    <div className="mt-2 text-xs text-slate-500">Dept: {fmt(e.department)} • Supervisor: {fmt(e.supervisorName || e.supervisor_name)} • {fmt(e.supervisorEmail)}</div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-slate-500">No experience listed.</div>
              )}
            </div>
          )}
        </div>

        {/* Row 6: List of Education */}
        <div className="md:col-span-2">
          {section(
            "Education",
            <div className="space-y-3">
              {(profile.education || []).length ? (
                profile.education.map((edu, idx) => (
                  <div key={idx} className="p-3 rounded-lg bg-slate-50 border border-gray-100">
                    <div className="font-semibold">{fmt(edu.degree)}</div>
                    <div className="text-xs text-slate-500">{fmt(edu.institute)} • {fmt(edu.passingYear || edu.year || edu.startDate)}</div>
                    <div className="text-sm mt-1">Subject: {fmt(edu.subject)} • Grade: {fmt(edu.cgpa || edu.result_v || edu.cgpa)}</div>
                    <div className="text-xs text-slate-500 mt-1">Achievement: {fmt(edu.achievement)}</div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-slate-500">No education records.</div>
              )}
            </div>
          )}
        </div>

        {/* Row 7: List of Training */}
        <div className="md:col-span-2">
          {section(
            "Training",
            <div className="space-y-2">
              {(profile.training || []).length ? (
                profile.training.map((t, i) => (
                  <div key={i} className="p-3 rounded-md bg-slate-50 border border-gray-100">
                    <div className="font-semibold">{fmt(t.trainingTitle)}</div>
                    <div className="text-xs text-slate-500">{fmt(t.institution)} • {fmt(t.country)} • {fmt(t.modality)}</div>
                    <div className="text-sm mt-1">{fmt(t.learningArea)}</div>
                    <div className="text-xs text-slate-500 mt-1">{fmt(t.startDate)} — {fmt(t.endDate)}</div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-slate-500">No training records.</div>
              )}
            </div>
          )}
        </div>

        {/* Row 8: List of Certificate */}
        <div className="md:col-span-2">
          {section(
            "Certificates",
            <div className="space-y-2">
              {(profile.certificates || []).length ? (
                profile.certificates.map((c, i) => (
                  <div key={i} className="p-3 rounded-md bg-slate-50 border border-gray-100">
                    <div className="font-semibold">{fmt(c.certification)}</div>
                    <div className="text-xs text-slate-500">{fmt(c.institution)} • {fmt(c.country)} • {fmt(c.status)}</div>
                    <div className="text-xs mt-1">Enrolled ID: {fmt(c.enrolledId)}</div>
                    <div className="text-sm mt-1">Validity: {fmt(c.startDate)} — {fmt(c.endDate)}</div>
                    <div className="text-xs text-slate-500 mt-1">URL: {c.certificationUrl ? (<a className="text-indigo-600 underline" href={c.certificationUrl}>{c.certificationUrl}</a>) : 'N/A'}</div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-slate-500">No certificates.</div>
              )}
            </div>
          )}
        </div>

        {/* Row 9: List of Publication */}
        <div className="md:col-span-2">
          {section(
            "Publications",
            <div className="space-y-2">
              {(profile.publications || []).length ? (
                profile.publications.map((p, i) => (
                  <div key={i} className="p-3 rounded-md bg-slate-50 border border-gray-100">
                    <div className="font-semibold">{fmt(p.title)}</div>
                    <div className="text-xs text-slate-500">{fmt(p.authors)} • {fmt(p.publisher)} • {fmt(p.year)}</div>
                    <div className="text-sm mt-1">Link: {p.link ? (<a className="text-indigo-600 underline" href={p.link}>{p.link}</a>) : 'N/A'}</div>
                    <div className="text-xs text-slate-500 mt-1">Remarks: {fmt(p.remarks)}</div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-slate-500">No publications.</div>
              )}
            </div>
          )}
        </div>

        {/* Row 10: Personal Information (many fields) */}
        <div className="md:col-span-2">
          {section(
            "Personal Information",
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-700">
              <div>
                <div className="font-medium">Date of Birth</div>
                <div className="text-xs text-slate-600">{fmt(personal.dob)}</div>

                <div className="font-medium mt-3">Father's Name</div>
                <div className="text-xs text-slate-600">{fmt(personal.fatherName)}</div>

                <div className="font-medium mt-3">Mother's Name</div>
                <div className="text-xs text-slate-600">{fmt(personal.motherName)}</div>

                <div className="font-medium mt-3">Religion</div>
                <div className="text-xs text-slate-600">{fmt(personal.religion)}</div>

                <div className="font-medium mt-3">Country of Birth</div>
                <div className="text-xs text-slate-600">{fmt(personal.countryOfBirth)}</div>

                <div className="font-medium mt-3">Nationality Type</div>
                <div className="text-xs text-slate-600">{fmt(personal.nationalityType)}</div>

                <div className="font-medium mt-3">Nationality</div>
                <div className="text-xs text-slate-600">{fmt(personal.nationality)}</div>
              </div>

              <div>
                <div className="font-medium">Gender</div>
                <div className="text-xs text-slate-600">{fmt(personal.gender)}</div>

                <div className="font-medium mt-3">Marital Status</div>
                <div className="text-xs text-slate-600">{fmt(personal.maritalStatus)}</div>

                <div className="font-medium mt-3">Spouse Name</div>
                <div className="text-xs text-slate-600">{fmt(personal.spouseName)}</div>

                <div className="font-medium mt-3">Blood Group</div>
                <div className="text-xs text-slate-600">{fmt(personal.bloodGroup)}</div>

                <div className="font-medium mt-3">NID Number</div>
                <div className="text-xs text-slate-600">{fmt(personal.nidNumber)}</div>

                <div className="font-medium mt-3">Passport</div>
                <div className="text-xs text-slate-600">{fmt(personal.passport)} ({fmt(personal.passportIssueDate)} — {fmt(personal.passportExpiryDate)})</div>

                <div className="font-medium mt-3">Disability</div>
                <div className="text-xs text-slate-600">{fmt(personal.disability)}</div>
              </div>
            </div>
          )}
        </div>
		<div className="md:col-span-2">
          {section(
			"Address",
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-700">
				<div>
					<div className="font-medium">Present Address</div>
					<div className="text-xs text-slate-600">{fmt(presentAddress.fullAddress)}</div>
					<div className="font-medium">Division</div>
					<div className="text-xs text-slate-600">{fmt(presentAddress.division)}</div>
					<div className="font-medium">District</div>
					<div className="text-xs text-slate-600">{fmt(presentAddress.district)}</div>
					<div className="font-medium">Postal Code</div>
					<div className="text-xs text-slate-600">{fmt(presentAddress.postalCode)}</div>
					<div className="font-medium">Thana</div>
					<div className="text-xs text-slate-600">{fmt(presentAddress.thana)}</div>
				</div> 
				<div>
					<div className="font-medium">Permanent Address</div>
					<div className="text-xs text-slate-600">{fmt(permanentAddress.fullAddress)}</div>
					<div className="font-medium">Division</div>
					<div className="text-xs text-slate-600">{fmt(permanentAddress.division)}</div>
					<div className="font-medium">District</div>
					<div className="text-xs text-slate-600">{fmt(permanentAddress.district)}</div>
					<div className="font-medium">Postal Code</div>
					<div className="text-xs text-slate-600">{fmt(permanentAddress.postalCode)}</div>
					<div className="font-medium">Thana</div>
					<div className="text-xs text-slate-600">{fmt(permanentAddress.thana)}</div>
				</div> 
			</div>
		  )}
        </div>
        {/* Row 11: EX CBIU Employee */}
        <div className="md:col-span-2">{section("EX CBIU Employee", <div className="text-sm text-slate-700">Employee ID: {fmt(exEmployee.employeeId)} • Joining Date: {fmt(exEmployee.joiningDate)} • Last Working Day: {fmt(exEmployee.lastWorkingDay)} • Supervisor: {fmt(exEmployee.supervisorName)} ({fmt(exEmployee.supervisorDesignation)}) • Supervisor Phone/Email: {fmt(exEmployee.supervisorPhone)} / {fmt(exEmployee.supervisorEmail)}</div>)}</div>

        {/* Row 12: References */}
        <div className="md:col-span-2">
          {section(
            "References",
            <div className="space-y-2 text-sm">
              {(profile.references || []).length ? (
                profile.references.map((r, i) => (
                  <div key={i} className="p-2 border rounded-md bg-slate-50">
                    <div className="font-semibold">{fmt(r.name)}</div>
                    <div className="text-xs text-slate-500">{fmt(r.designation || r.relation)} • {fmt(r.organisation || r.organisation || r.organisation)}</div>
                    <div className="text-xs text-slate-500">Address: {fmt(r.address)}</div>
                    <div className="text-xs text-slate-500">Phone: {fmt(r.phone)} • Email: {fmt(r.email)}</div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-slate-500">No references provided.</div>
              )}
            </div>
          )}
        </div>

        {/* Row 13: Extra Curricular Activities */}
        <div className="md:col-span-2">{section("Extra Curricular Activities", <div className="text-sm text-slate-700" dangerouslySetInnerHTML={{__html: textformat(fmt(profile.activities))}}></div>)}</div> 

        {/* Row 14: Languages */}
        <div className="md:col-span-2">
          {section(
            "Languages",
            <div className="space-y-1 text-sm">
              {(profile.languages || []).length ? (
                profile.languages.map((l, i) => (
                  <div key={i}>{fmt(l.language)} — Reading: {fmt(l.reading)}, Writing: {fmt(l.writing)}, Speaking: {fmt(l.speaking)}</div>
                ))
              ) : (
                <div className="text-sm text-slate-500">No languages listed.</div>
              )}
            </div>
          )}
        </div>

        {/* Row 15: Hobbies, Interests, Industries */}
        <div className="md:col-span-2">
          {section(
            "Hobbies, Interests & Preferred Industries",
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-slate-700">
              <div>
                <div className="font-medium">Hobbies</div>
                <div className="text-xs text-slate-600">{fmt(profile.hobbies)}</div>
              </div>
              <div>
                <div className="font-medium">Interested Industries</div>
                <div className="text-xs text-slate-600">{fmt(profile.industries)}</div>
              </div>
              <div>
                <div className="font-medium">Expected Salary</div>
                <div className="text-xs text-slate-600">Current: {fmt(profile.salary?.currentSalary)}k • Expected: {fmt(profile.salary?.expectedSalary)}k • Notice: {fmt(profile.salary?.noticePeriod)} months</div>
              </div>
            </div>
          )}
        </div>

        {/* E-signature (final) */}
        <div className="md:col-span-2">{section("E-Signature", <div className="flex items-center justify-between"><div><div className="text-sm text-slate-500">Declaration / E-Signature</div><div className="text-lg font-semibold mt-1">{fmt(profile.declaration)}</div></div><div className="text-xs text-slate-400">Date: {new Date().toLocaleDateString()}</div></div>)}</div>
      </div>
    </motion.div>
  );
}
